import React from 'react';
import withStyles from 'isomorphic-style-loader/withStyles';
import Hero from '../../components/marketing/Hero';
import CityLinks from '../../components/marketing/CityLinks';
import CtaBanner from '../../components/marketing/CtaBanner';
import Link from '../../components/Link';
import s from './ServicesHub.css';

// All-Services hub — an ADDITIVE "browse everything" page. Every card links into the EXISTING
// /services/:service SEO pages (and the city grid feeds /services/:service/:cityState). This page does
// NOT modify those routes; it only links to them.
//
// Category list: the canonical service slugs live in the DB (built by core/seo/syncCategorySeo.js),
// with no clean static export in the repo. Per the plan, we use a sensible static list of the real
// BestHand categories using the canonical public SEO slugs (e.g. electrical -> electrical-help,
// tv mounting -> tv-mounting), grouped for scannability. Slugs mirror SeoService.slug.
// COMPLIANCE: "from $X" figures are illustrative starting prices, not quotes — labeled as such below.
// LEGAL_REVIEW_REQUIRED: "from $X" starting-price figures are placeholders until real rates are confirmed.

// Simple inline icon set (no photos — clean line icons only, per plan §10 visuals).
const ICONS = {
  bolt: 'M13 2 4 14h7l-1 8 9-12h-7l1-8Z',
  drop: 'M12 3s6 6.5 6 11a6 6 0 0 1-12 0c0-4.5 6-11 6-11Z',
  spray: 'M9 11h6v9a2 2 0 0 1-2 2h-2a2 2 0 0 1-2-2v-9ZM12 3v4M9 5h6M16 4l2 1M16 7l2 1',
  roller: 'M3 5h14v5H3zM10 10v4H8v6h4v-6h-2',
  wind: 'M3 8h11a3 3 0 1 0-3-3M3 12h16a3 3 0 1 1-3 3M3 16h9a3 3 0 1 1-3 3',
  fridge: 'M6 2h12a1 1 0 0 1 1 1v18a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1V3a1 1 0 0 1 1-1ZM5 9h14M9 5v2M9 12v3',
  tv: 'M3 5h18v11H3zM8 21h8M12 16v5',
  chair: 'M6 4v8h12V4M6 12l-1 8M18 12l1 8M5 16h14',
  leaf: 'M11 20A7 7 0 0 1 4 13c0-5 4-9 16-9 0 12-4 16-9 16Zm0 0c0-6 3-9 8-10',
  tools: 'M14.7 6.3a4 4 0 0 0-5.4 5.4L3 18v3h3l6.3-6.3a4 4 0 0 0 5.4-5.4l-2.5 2.5-2.1-2.1 2.6-2.4Z',
  truck: 'M1 6h13v9H1zM14 9h4l3 3v3h-7M5.5 18a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3ZM17.5 18a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3Z',
  trash: 'M4 7h16M9 7V4h6v3M6 7l1 13h10l1-13',
};

// Each group → cards → /services/<slug>. Slugs verified against canonicalServiceSlug aliases.
const GROUPS = [
  {
    title: 'Home repairs',
    items: [
      { name: 'Handyman', slug: 'handyman', icon: ICONS.tools, from: 45, blurb: 'General fixes, mounting, odd jobs around the house.' },
      { name: 'Electrical', slug: 'electrical-help', icon: ICONS.bolt, from: 60, blurb: 'Outlets, fixtures, switches and small wiring jobs.' },
      { name: 'Plumbing', slug: 'plumbing', icon: ICONS.drop, from: 60, blurb: 'Leaks, faucets, toilets, garbage disposals and more.' },
      { name: 'Appliances', slug: 'appliance-installation', icon: ICONS.fridge, from: 55, blurb: 'Install or hook up your appliances the right way.' },
    ],
  },
  {
    title: 'Cleaning',
    items: [
      { name: 'Cleaning', slug: 'cleaning', icon: ICONS.spray, from: 35, blurb: 'Homes, apartments, move-in / move-out deep cleans.' },
    ],
  },
  {
    title: 'Installations',
    items: [
      { name: 'TV Mounting', slug: 'tv-mounting', icon: ICONS.tv, from: 50, blurb: 'Securely mounted, leveled and cable-managed.' },
      { name: 'Furniture Assembly', slug: 'furniture-assembly', icon: ICONS.chair, from: 40, blurb: 'Flat-pack and custom furniture, built fast.' },
      { name: 'Painting', slug: 'painting', icon: ICONS.roller, from: 45, blurb: 'Walls, rooms and touch-ups with a clean finish.' },
      { name: 'HVAC', slug: 'hvac', icon: ICONS.wind, from: 70, blurb: 'Heating, cooling and ventilation help.' },
    ],
  },
  {
    title: 'Moving & outdoor',
    items: [
      { name: 'Moving Help', slug: 'moving-help', icon: ICONS.truck, from: 50, blurb: 'Loading, unloading and heavy lifting.' },
      { name: 'Junk Removal', slug: 'junk-removal', icon: ICONS.trash, from: 50, blurb: 'Haul away furniture, debris and clutter.' },
      { name: 'Yard Work', slug: 'yard-work', icon: ICONS.leaf, from: 40, blurb: 'Mowing, weeding, leaves and seasonal cleanup.' },
    ],
  },
];

const ALL_ITEMS = GROUPS.flatMap((g) => g.items);

// ItemList JSON-LD of categories (links into the existing service pages).
const jsonLd = {
  '@context': 'https://schema.org',
  '@type': 'ItemList',
  name: 'BestHand Home Services',
  itemListElement: ALL_ITEMS.map((it, i) => ({
    '@type': 'ListItem',
    position: i + 1,
    name: it.name,
    url: `/services/${it.slug}`,
  })),
};

function ServicesHub() {
  return (
    <main className={s.page}>
      {/* SECURITY: escape </script> breakouts in JSON-LD (defense-in-depth). */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd).replace(/</g, '\\u003c').replace(/>/g, '\\u003e').replace(/&/g, '\\u0026') }}
      />

      <Hero
        eyebrow="All services"
        title="Every home service, one trusted app."
        subtitle="Browse what you need done, see upfront pricing, and book a vetted local pro — often matched in under 60 seconds."
        primaryLabel="Book a pro"
        primaryTo="/category?type=all"
        secondaryLabel="Get an estimate"
        secondaryTo="/pricing"
        badges={['Vetted local pros', 'Upfront pricing', 'Secure in-app payments']}
      />

      {/* Quick jump nav across groups */}
      <section className={s.secTight}>
        <div className={s.wrap}>
          <nav className={s.jump} aria-label="Service categories">
            {ALL_ITEMS.map((it) => (
              <Link key={it.slug} to={`/services/${it.slug}`} className={s.chip}>
                {it.name}
              </Link>
            ))}
          </nav>
        </div>
      </section>

      {GROUPS.map((group, gi) => (
        <section className={`${s.sec} ${gi % 2 === 1 ? s.alt : ''}`} key={group.title}>
          <div className={s.wrap}>
            <h2 className={s.h2}>{group.title}</h2>
            <div className={s.grid}>
              {group.items.map((it) => (
                <Link to={`/services/${it.slug}`} className={s.card} key={it.slug}>
                  <span className={s.ic}>
                    <svg viewBox="0 0 24 24" aria-hidden="true">
                      <path d={it.icon} fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </span>
                  <span className={s.cardBody}>
                    <span className={s.cardName}>{it.name}</span>
                    <span className={s.cardBlurb}>{it.blurb}</span>
                    {/* LEGAL_REVIEW_REQUIRED: illustrative starting price — not a quote. */}
                    <span className={s.cardFrom}>from ${it.from}</span>
                  </span>
                  <svg viewBox="0 0 24 24" aria-hidden="true" className={s.arrow}>
                    <path d="m9 18 6-6-6-6" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </Link>
              ))}
            </div>
            <p className={s.note}>Starting prices shown are illustrative; you’ll always see your full price before you book.</p>
          </div>
        </section>
      ))}

      {/* Popular services by city (SEO link grid → existing service×city pages) */}
      <section className={`${s.sec} ${s.alt}`}>
        <div className={s.wrap}>
          <CityLinks />
        </div>
      </section>

      <CtaBanner
        title="Don’t see your exact job?"
        subtitle="Most home tasks fit one of our categories — tell us what you need and we’ll match you with the right pro."
        primaryLabel="Book a pro"
        primaryTo="/category?type=all"
        secondaryLabel="Visit the Help Center"
        secondaryTo="/help"
      />
    </main>
  );
}

export default withStyles(s)(ServicesHub);
